/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// Soft UI Dashboard PRO React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard PRO React context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Cognito utilities
import ProfileSelector from "components/ProfileSelector";

// Real task notifications (reuses the same data the Tasks page shows)
import { getTasks } from "layouts/dashboardLayout/pages/Users/services/graphqlService";
import {
  extractTasksFromServiceResponse,
  isTaskStatusNew,
  sortTasksByNewest,
  buildTaskDescription,
  getTaskCreatedTime,
  formatTaskAge,
  taskId,
  prettifyLabel,
} from "layouts/dashboardLayout/pages/tasksParse";


function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } =
    controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(null);
  const [notificationTasks, setNotificationTasks] = useState([]);
  const route = useLocation().pathname.split("/").slice(1);
  const auth = useAuth();
  const navigate = useNavigate();

  // Task-based notifications: any task still in "New" status is an unread
  // notification for this user. Reuses the same getTasks call and parsing
  // helpers the Tasks page already relies on, so no new backend work is
  // needed here.
  const fetchNotificationTasks = useCallback(async () => {
    if (auth?.isGuest || !auth?.user?.id_token) {
      setNotificationTasks([]);
      return;
    }
    try {
      const res = await getTasks(auth.user.id_token);
      const { list } = extractTasksFromServiceResponse(res);
      const newTasks = sortTasksByNewest(list.filter(isTaskStatusNew));
      setNotificationTasks(newTasks);
    } catch (err) {
      // Notifications are best-effort; leave the previous list in place
      // rather than surfacing an error in the navbar.
      console.warn("[DashboardNavbar] Failed to load notifications:", err?.message || err);
    }
  }, [auth?.isGuest, auth?.user?.id_token]);

  useEffect(() => {
    fetchNotificationTasks();
    const intervalId = setInterval(fetchNotificationTasks, 60000);
    return () => clearInterval(intervalId);
  }, [fetchNotificationTasks]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => {
    setOpenMenu(event.currentTarget);
    fetchNotificationTasks();
  };
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenUserMenu = (event) => setOpenUserMenu(event.currentTarget);
  const handleCloseUserMenu = () => setOpenUserMenu(null);

  const handleLogout = () => {
    handleCloseUserMenu();
    auth.signOut();
    navigate("/sign-in", { replace: true });
  };

  // Render the notifications menu, sourced from real "New" tasks
  const handleNotificationClick = () => {
    handleCloseMenu();
    navigate("/tasks");
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {notificationTasks.length === 0 ? (
        <MenuItem disabled sx={{ py: 1.5 }}>
          <SoftTypography variant="button" color="secondary" fontWeight="regular">
            No new notifications
          </SoftTypography>
        </MenuItem>
      ) : (
        notificationTasks.slice(0, 8).map((task) => (
          <NotificationItem
            key={taskId(task)}
            color="primary"
            image={
              <Icon
                fontSize="small"
                sx={{ color: ({ palette: { white } }) => white.main }}
              >
                assignment
              </Icon>
            }
            title={[
              prettifyLabel(task?.taskCategory) || "New task",
              buildTaskDescription(task),
            ]}
            date={formatTaskAge(getTaskCreatedTime(task)) || ""}
            onClick={handleNotificationClick}
          />
        ))
      )}
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
          {/* <Icon
            fontSize="medium"
            sx={navbarDesktopMenu}
            onClick={handleMiniSidenav}
          >
            {miniSidenav ? "menu_open" : "menu"}
          </Icon> */}
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* <SoftBox pr={1}>
              <SoftInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox> */}
            <SoftBox color={light ? "white" : "inherit"}>
              {(auth.isAuthenticated && auth.user) || auth.isGuest ? (
                <>
                  <IconButton
                    sx={navbarIconButton}
                    size="small"
                    onClick={handleOpenUserMenu}
                  >
                    <Icon
                      sx={({ palette: { dark, white } }) => ({
                        color: light ? white.main : dark.main,
                      })}
                    >
                      account_circle
                    </Icon>
                    <SoftTypography
                      variant="button"
                      fontWeight="medium"
                      color={light ? "white" : "dark"}
                      ml={1}
                    >
                      {auth.isGuest
                        ? "Guest"
                        : auth.user.profile?.email ||
                          auth.profile?.email ||
                          "User"}
                    </SoftTypography>
                  </IconButton>
                  <Menu
                    anchorEl={openUserMenu}
                    open={Boolean(openUserMenu)}
                    onClose={handleCloseUserMenu}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    PaperProps={{
                      sx: {
                        minWidth: 280,
                        mt: 1.5,
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <SoftBox px={2} py={2}>
                      <SoftTypography
                        variant="body2"
                        fontWeight="bold"
                        color="dark"
                      >
                        {auth.isGuest
                          ? "Guest"
                          : auth.user.profile?.email ||
                            auth.profile?.email ||
                            "—"}
                      </SoftTypography>
                      <SoftTypography
                        variant="caption"
                        color="text"
                        display="block"
                        mt={0.25}
                      >
                        {auth.isGuest
                          ? "Sample data · read-only"
                          : auth.tokenEntityType && auth.tokenRole
                            ? `${auth.tokenEntityType} · ${auth.tokenRole}`
                            : auth.tokenRole || auth.tokenEntityType || "User"}
                      </SoftTypography>
                    </SoftBox>
                    <Divider />
                    {!auth.isGuest && auth.associations?.length > 1 && (
                      <>
                        <SoftBox px={2} py={1}>
                          <SoftTypography
                            variant="caption"
                            color="text"
                            fontWeight="bold"
                          >
                            Switch profile
                          </SoftTypography>
                        </SoftBox>
                        <ProfileSelector
                          embedded
                          onCloseParent={handleCloseUserMenu}
                        />
                        <Divider />
                      </>
                    )}
                    <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                      <Icon fontSize="small" sx={{ mr: 1.5 }}>
                        logout
                      </Icon>
                      Sign Out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Link to="/sign-in">
                  <IconButton sx={navbarIconButton} size="small">
                    <Icon
                      sx={({ palette: { dark, white } }) => ({
                        color: light ? white.main : dark.main,
                      })}
                    >
                      account_circle
                    </Icon>
                    <SoftTypography
                      variant="button"
                      fontWeight="medium"
                      color={light ? "white" : "dark"}
                    >
                      Sign in
                    </SoftTypography>
                  </IconButton>
                </Link>
              )}
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton> */}
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge
                  badgeContent={notificationTasks.length}
                  color="error"
                  max={9}
                  overlap="circular"
                  invisible={notificationTasks.length === 0}
                >
                  <Icon className={light ? "text-white" : "text-dark"}>
                    notifications
                  </Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
